import { PGroup, PProduct } from '@attensi/portal-models'

const alphabeticalNameSort = (a: { name: string }, b: { name: string }) =>
  a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1

export interface FilterGroup {
  id: number
  name: string
  product?: string
  description?: string
  children?: FilterGroup[]
  isSelected?: boolean
  isInitialFilter?: boolean
  product_id?: number
  // Products: false once the product is discarded
  active?: boolean
  // Modules: null when the module has no category
  module_category_id?: number | null
}

export const groupsToFilterGroups = (groups: PGroup[]): FilterGroup[] => {
  // Initialize groups without parent or children
  const tempGroups = groups.map((g) => ({
    id: g.id,
    name: g.name,
    parent_id: g.parent_id,
    children: [] as unknown[],
  }))

  // Add children to FilterGroups
  tempGroups.forEach((tg) => {
    tg.children = tempGroups.filter((d) => d.parent_id === tg.id)
  })

  const mapToFilterGroup = (eg: FilterGroup): FilterGroup => ({
    id: eg.id,
    name: eg.name,
    description: eg.description,
    children: !eg.children
      ? []
      : eg.children.map(mapToFilterGroup).sort(alphabeticalNameSort),
  })

  // Return only root FilterGroups
  return tempGroups
    .filter((tg) => !tg.parent_id)
    .map((tg) => mapToFilterGroup(tg as FilterGroup))
    .sort(alphabeticalNameSort)
}

export const productsToFilterGroups = (products: PProduct[]): FilterGroup[] => {
  return products
    .filter((p) => p.active)
    .map((p) => ({
      id: p.id,
      name: p.title || `id: ${p.id}`,
      description: p.type.description,
    }))
    .sort(alphabeticalNameSort)
}

/**
 * A sub product is generated by the portal from a module category of another
 * product. A selected product's description is the portal's type description
 * ('Sub Product') from the product list, looked up by id for a saved report
 * (helper.reportSelections). The module filter never lists a module under one,
 * so the Rearrange Modules window has nothing to arrange for it, while the file
 * still gives it a block of the modules it shares with its parent.
 */
export const isSubProduct = (product: FilterGroup): boolean =>
  String(product?.description ?? '')
    .trim()
    .toLowerCase() === 'sub product'

export const groupItemsByProductId = (arr: FilterGroup[]) => {
  const grouped = arr.reduce((acc: any, module: FilterGroup) => {
    const key = module?.product_id?.toString() || ''
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(module)
    return acc
  }, {} as Record<string, FilterGroup[]>)
  return grouped
}

/**
 * Product groups in the order the Rearrange Modules window lists them and the
 * report lays out its product blocks: alphabetical by product name (case
 * folded), groups without a product name last, then by key.
 */
export const orderProductGroups = (
  groups: Record<string, FilterGroup[]>
): string[] => {
  const nameOf = (key: string) => String(groups[key]?.[0]?.product ?? '')
  return Object.keys(groups).sort((a, b) => {
    const na = nameOf(a)
    const nb = nameOf(b)
    if (na === '' || nb === '') return na === nb ? 0 : na === '' ? 1 : -1
    return (
      na.localeCompare(nb, undefined, { sensitivity: 'base' }) ||
      Number(a) - Number(b) ||
      0
    )
  })
}

export const flattenGroupedFilterGroups = (
  groups: Record<string, FilterGroup[]>
): FilterGroup[] =>
  orderProductGroups(groups).reduce<FilterGroup[]>(
    (acc, key) => acc.concat(groups[key]),
    []
  )

/**
 * True when the modules inside every product group are already in the
 * (case-insensitive) alphabetical order the module filter produces, i.e. the
 * user has not arranged a custom order. Grouping mirrors groupItemsByProductId.
 */
export const isAlphabeticalWithinProducts = (
  modules: FilterGroup[]
): boolean => {
  const nameOf = (m: FilterGroup) => String(m?.name ?? '').toLowerCase()
  return Object.values(groupItemsByProductId(modules)).every((group) =>
    (group as FilterGroup[]).every(
      (m, i) => i === 0 || nameOf((group as FilterGroup[])[i - 1]) <= nameOf(m)
    )
  )
}

/**
 * Flattens the groups in the given key order (the arranged product order), then
 * any remaining group in alphabetical product order. The backend ranks modules
 * by their position in this flat list, so it must follow what the window shows.
 */
export const flattenGroupsInOrder = (
  groups: Record<string, FilterGroup[]>,
  keys: string[]
): FilterGroup[] => {
  const seen = new Set<string>()
  const ordered = [
    ...keys.filter((k) => groups[k]),
    ...orderProductGroups(groups),
  ].filter((k) => (seen.has(k) ? false : (seen.add(k), true)))
  return ordered.reduce<FilterGroup[]>((acc, k) => acc.concat(groups[k]), [])
}

export const reorderList = <T>(
  list: T[],
  fromIndex: number,
  toIndex: number
): void => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.length ||
    toIndex >= list.length
  ) {
    return
  }

  const [movedItem] = list.splice(fromIndex, 1)
  list.splice(toIndex, 0, movedItem)
}
